import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Layout } from "@/components/site/Layout";
import { getRealYield } from "@/lib/real-yield.functions";
import { classifyRegime, type RealYieldRegime } from "@/lib/real-yield";

const SITE = "https://xauproacademy.com";
const PATH = "/gold-real-yield";
const TITLE = "US 10Y Real Yield and Gold: TIPS Tracker for XAUUSD | XAU Pro Academy";
const DESC =
  "Free 10-year real yield (TIPS) tracker for gold traders. Official FRED data, daily trend, regime classification, and how gold moved alongside real rates, explained simply.";

const FAQ = [
  { q: "What is the 10-year real yield?", a: "It is the yield on 10-year US Treasury Inflation-Protected Securities (TIPS). Because TIPS payments adjust for inflation, their yield represents the real, after-inflation return investors demand for holding US government debt for ten years." },
  { q: "Why does the real yield matter so much for gold?", a: "Gold pays no interest. The real yield is the opportunity cost of holding gold instead of a risk-free inflation-protected bond. When real yields fall or turn negative, holding gold becomes cheaper in relative terms and demand tends to rise. When real yields climb, gold faces a structural headwind." },
  { q: "Is the relationship between real yields and gold perfect?", a: "No. It is one of the strongest macro drivers over medium horizons, but central bank buying, geopolitical stress and currency dynamics can lift gold even while real yields rise. Treat it as a regime indicator, not a mechanical rule." },
  { q: "Where does this data come from?", a: "Daily 10-year TIPS and nominal Treasury yields come from the official FRED database of the Federal Reserve Bank of St. Louis (series DFII10 and DGS10). Gold monthly returns come from our XAUUSD monthly history." },
  { q: "How often is the data updated?", a: "FRED publishes each business day, usually in the late US afternoon. This page refreshes hourly and keeps the last successful reading if the source is temporarily unavailable." },
];

const REGIME_TEXT: Record<RealYieldRegime, { label: string; text: string; tone: string }> = {
  supportive: { label: "Supportive for gold", text: "Real yields are negative or clearly falling. The opportunity cost of holding gold is low, which historically supports demand.", tone: "text-gold" },
  neutral: { label: "Neutral regime", text: "Real yields are moderate and stable. The macro channel is not forcing direction; structure and liquidity lead.", tone: "text-muted-foreground" },
  headwind: { label: "Headwind for gold", text: "Real yields are elevated or rising. Holding a zero-yield asset is expensive in relative terms, which historically pressures gold.", tone: "text-destructive" },
};

export const Route = createFileRoute("/gold-real-yield")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "keywords", content: "10 year real yield gold, TIPS and gold price, real interest rates XAUUSD, DFII10 gold, real yields gold correlation, gold macro driver" },
      { property: "og:title", content: "US 10Y Real Yield and Gold: TIPS Tracker" },
      { property: "og:description", content: "Official FRED real yield data with gold reaction per month. The single most important macro driver of XAUUSD, explained clearly." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}${PATH}` },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: "US 10-Year Real Yield (TIPS) and Gold Monthly Reaction",
          description: "Daily US 10-year real and nominal Treasury yields with XAUUSD monthly returns aligned to each month.",
          url: `${SITE}${PATH}`,
          license: "https://xauproacademy.com/terms", isAccessibleForFree: true, inLanguage: "en", variableMeasured: ["10Y TIPS real yield", "10Y nominal Treasury yield", "XAUUSD monthly return"], temporalCoverage: "2021-01-01/..", distribution: [{ "@type": "DataDownload", encodingFormat: "text/html", contentUrl: `${SITE}${PATH}` }], dateModified: new Date().toISOString().slice(0, 10), creator: { "@type": "Organization", name: "XAU Pro Academy", url: SITE },
          isBasedOn: "https://fred.stlouisfed.org/series/DFII10",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Real Yield and Gold", item: `${SITE}${PATH}` },
          ],
        }),
      },
    ],
  }),
  loader: () => getRealYield(),
  staleTime: 60 * 60 * 1000,
  component: RealYieldPage,
});

const monthLabel = (p: string) => {
  const [y, m] = p.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
};
const fp = (x: number | null, sign = false) => (x == null ? "n/a" : `${sign && x > 0 ? "+" : ""}${x.toFixed(2)}%`);
const fpp = (x: number | null) => (x == null ? "n/a" : `${x > 0 ? "+" : ""}${x.toFixed(2)} pp`);

function RealYieldPage() {
  const data = Route.useLoaderData();
  const latest = data.latest;
  const [vars, setVars] = useState<Record<string, string>>({});
  useEffect(() => {
    const cs = getComputedStyle(document.documentElement);
    const out: Record<string, string> = {};
    for (const k of ["gold", "chart-silver", "border", "muted-foreground", "destructive", "card", "foreground", "muted"]) out[k] = cs.getPropertyValue(`--${k}`).trim();
    setVars(out);
  }, []);
  const c = (k: string) => vars[k] || "currentColor";

  const yearSeries = useMemo(() => data.days.slice(-260).map((d) => ({ ...d, label: d.date.slice(5) })), [data.days]);
  const monthly = useMemo(() => data.months.slice(-24).map((m) => ({ ...m, label: monthLabel(m.period) })), [data.months]);
  const regime = latest ? classifyRegime(latest.real, latest.monthlyChange) : null;
  const regimeUi = regime ? REGIME_TEXT[regime] : null;

  return (
    <Layout>
      <section className="relative overflow-hidden border-b border-border/60 bg-gradient-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="container relative mx-auto max-w-6xl px-4 pb-14 pt-16 md:pt-24">
          <span className="inline-flex items-center rounded-full border border-gold/30 bg-gold-muted px-3 py-1 font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
            Institutional Tools · FRED Data
          </span>
          <h1 className="mt-6 max-w-3xl font-display text-4xl font-bold tracking-tight md:text-5xl">
            US 10Y Real Yield and Gold: <span className="text-gradient-gold">the macro driver</span> of XAUUSD
          </h1>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            The 10-year TIPS yield is the real, after-inflation return on risk-free US debt, and the single most watched
            opportunity cost of holding gold. Track its daily level, its trend, and how gold behaved alongside it.
          </p>
          {latest && (
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              Latest reading {latest.date} · Updated each US business day · Source: Federal Reserve Bank of St. Louis (FRED)
            </p>
          )}
        </div>
      </section>

      {!latest ? (
        <section className="container mx-auto max-w-6xl px-4 py-16 text-center text-muted-foreground">{data.error ?? "Real yield data is temporarily unavailable."}</section>
      ) : (
        <>
          {data.stale && <p role="status" className="container mx-auto max-w-6xl px-4 pt-6 text-sm text-gold">The source is temporarily unavailable. Showing the last successfully retrieved data.</p>}

          <section className="container mx-auto max-w-6xl px-4 pt-10">
            <div className="rounded-xl border border-gold/30 bg-card p-6">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">Current regime</p>
              <div className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-2">
                <span className={`font-display text-2xl font-bold ${regimeUi?.tone}`}>{regimeUi?.label}</span>
                <span className="font-mono text-sm text-muted-foreground">10Y real yield {fp(latest.real)} · {fpp(latest.monthlyChange)} this month</span>
              </div>
              <p className="mt-2 max-w-3xl text-sm text-muted-foreground">{regimeUi?.text}</p>
              <p className="mt-4 text-xs text-muted-foreground">Context for bias only. Not a trade signal.</p>
            </div>
          </section>

          <section className="container mx-auto max-w-6xl px-4 py-10">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="10Y real yield (TIPS)" value={fp(latest.real)} sub={`as of ${latest.date}`} tone={latest.real < 0 ? "text-gold" : ""} />
              <Stat label="Weekly change" value={fpp(latest.weeklyChange)} sub="5 trading days" tone={latest.weeklyChange == null ? "" : latest.weeklyChange < 0 ? "text-gold" : latest.weeklyChange > 0 ? "text-destructive" : ""} />
              <Stat label="Monthly change" value={fpp(latest.monthlyChange)} sub="21 trading days" tone={latest.monthlyChange == null ? "" : latest.monthlyChange < 0 ? "text-gold" : latest.monthlyChange > 0 ? "text-destructive" : ""} />
              <Stat label="10Y nominal yield" value={fp(latest.nominal)} sub="Treasury constant maturity" />
            </div>

            <div className="mt-10 rounded-xl border border-border bg-card p-5">
              <h2 className="font-display text-xl font-bold">Real yield, last 12 months</h2>
              <p className="mt-1 text-sm text-muted-foreground">Daily 10-year TIPS yield. The dashed line marks zero: below it, inflation-protected bonds guarantee a loss of purchasing power.</p>
              <div className="mt-4 h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={yearSeries}>
                    <CartesianGrid stroke={c("border")} strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fill: c("muted-foreground"), fontSize: 11 }} minTickGap={40} />
                    <YAxis tick={{ fill: c("muted-foreground"), fontSize: 11 }} unit="%" width={44} domain={["auto", "auto"]} />
                    <Tooltip contentStyle={{ background: c("card"), border: `1px solid ${c("border")}` }} labelStyle={{ color: c("foreground") }} itemStyle={{ color: c("foreground") }} cursor={{ fill: c("muted"), fillOpacity: 0.25 }} formatter={(v: number) => `${v.toFixed(2)}%`} />
                    <Legend />
                    <ReferenceLine y={0} stroke={c("muted-foreground")} strokeDasharray="4 4" />
                    <Line type="monotone" dataKey="real" name="10Y real yield" stroke={c("gold")} dot={false} strokeWidth={2} />
                    <Line type="monotone" dataKey="nominal" name="10Y nominal" stroke={c("chart-silver")} dot={false} strokeWidth={1.5} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {monthly.length > 0 && (
              <div className="mt-8 rounded-xl border border-border bg-card p-5">
                <h2 className="font-display text-xl font-bold">Gold vs the real yield trend, 24 months</h2>
                <p className="mt-1 text-sm text-muted-foreground">Bars: XAUUSD monthly return. Line: average 10Y real yield that month. Note how extended gold rallies often coincide with flat or falling real yields.</p>
                <div className="mt-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={monthly}>
                      <CartesianGrid stroke={c("border")} strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fill: c("muted-foreground"), fontSize: 11 }} minTickGap={30} />
                      <YAxis yAxisId="g" tick={{ fill: c("muted-foreground"), fontSize: 11 }} unit="%" width={44} />
                      <YAxis yAxisId="r" orientation="right" tick={{ fill: c("muted-foreground"), fontSize: 11 }} unit="%" width={44} domain={["auto", "auto"]} />
                      <Tooltip contentStyle={{ background: c("card"), border: `1px solid ${c("border")}` }} labelStyle={{ color: c("foreground") }} itemStyle={{ color: c("foreground") }} cursor={{ fill: c("muted"), fillOpacity: 0.25 }} />
                      <Legend />
                      <ReferenceLine yAxisId="g" y={0} stroke={c("muted-foreground")} />
                      <Bar yAxisId="g" dataKey="goldPct" name="Gold monthly return">
                        {monthly.map((r) => <Cell key={r.period} fill={(r.goldPct ?? 0) >= 0 ? c("gold") : c("destructive")} />)}
                      </Bar>
                      <Line yAxisId="r" type="monotone" dataKey="realAvg" name="Avg real yield" stroke={c("chart-silver")} dot={false} strokeWidth={2} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            <div className="mt-8 overflow-x-auto rounded-xl border border-border bg-card">
              <table className="w-full text-sm">
                <thead className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  <tr className="border-b border-border">
                    {["Month", "Avg real yield", "Change", "Gold return"].map((h) => <th key={h} className="px-4 py-3 text-left">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.months.slice(-12).reverse().map((m) => (
                    <tr key={m.period} className="border-b border-border/50">
                      <td className="px-4 py-2 font-mono">{monthLabel(m.period)}</td>
                      <td className="px-4 py-2">{fp(m.realAvg)}</td>
                      <td className={`px-4 py-2 ${m.realChange == null ? "" : m.realChange < 0 ? "text-gold" : m.realChange > 0 ? "text-destructive" : ""}`}>{fpp(m.realChange)}</td>
                      <td className={`px-4 py-2 ${m.goldPct == null ? "" : m.goldPct >= 0 ? "text-gold" : "text-destructive"}`}>{m.goldPct == null ? "n/a" : `${m.goldPct > 0 ? "+" : ""}${m.goldPct.toFixed(1)}%`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-10 rounded-xl border border-border bg-card p-6">
              <h2 className="font-display text-xl font-bold">How to read this as a gold trader</h2>
              <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-muted-foreground">
                <li>Falling real yields lower the cost of holding gold. Sustained declines often align with accumulation phases.</li>
                <li>Rising real yields are a structural headwind. Longs against a sharply rising real yield require stronger technical confirmation.</li>
                <li>The relationship is a regime filter, not a timing tool. Entries still require structure, liquidity interaction and displacement.</li>
                <li>Central bank demand and geopolitical stress can override the yield channel for months. Respect the price, not the model.</li>
              </ul>
              <div className="mt-4 flex flex-wrap gap-4 text-sm">
                <Link to="/gold-inflation-cpi" className="text-gold hover:underline">US CPI and Gold</Link>
                <Link to="/gold-cot-report" className="text-gold hover:underline">Gold COT Report</Link>
                <Link to="/gold-options-sentiment" className="text-gold hover:underline">Gold Options Sentiment</Link>
                <Link to="/macrolens" className="text-gold hover:underline">XAU MacroLens</Link>
                <Link to="/gold-analysis" className="text-gold hover:underline">Daily gold analysis</Link>
              </div>
            </div>
          </section>
        </>
      )}

      <section className="container mx-auto max-w-4xl px-4 pb-16">
        <h2 className="font-display text-2xl font-bold">Frequently asked questions</h2>
        <div className="mt-4 space-y-4">
          {FAQ.map((f) => (
            <div key={f.q} className="rounded-lg border border-border bg-card p-4">
              <h3 className="font-semibold">{f.q}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </Layout>
  );
}

function Stat({ label, value, sub, tone = "" }: { label: string; value: string; sub?: string; tone?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
      <p className={`mt-2 font-display text-2xl font-bold ${tone}`}>{value}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}
